// Chi vive a PopTown e di cosa parla (artboard 56). Ogni pop ha il suo ruolo; i ruoli «amici» si
// cercano e parlano di lavoro: PM e team leader di progetti, web designer e UX/UI di interfacce,
// analisti e data scientist di dati. Gli altri si salutano o fanno due chiacchiere. Le battute si
// alternano fra i due, una conversazione non si ripete finché ce ne sono altre. I discorsi stanno
// in talks.ts.
import { SMALL, TALKS, type Line } from './talks';

export type Role =
  | 'project-manager'
  | 'team-lead'
  | 'web-designer'
  | 'ux-ui'
  | 'analyst'
  | 'data-scientist'
  | 'developer'
  | 'qa'
  | 'devops'
  | 'reviewer'
  | 'security'
  | 'mail-clerk'
  | 'notifier'
  | 'researcher'
  | 'writer'
  | 'translator'
  | 'scheduler'
  | 'home'
  | 'triage';

/** gli abitanti, nell'ordine in cui entrano in scena: su uno schermo piccolo solo i primi */
export const CAST: Role[] = [
  'project-manager',
  'team-lead',
  'web-designer',
  'ux-ui',
  'analyst',
  'data-scientist',
  'developer',
  'qa',
  'mail-clerk',
  'researcher',
  'writer',
  'devops',
  'notifier',
  'reviewer',
];

const key = (a: Role, b: Role) => [a, b].sort().join('|');

/** chi cerca chi, con più probabilità */
export function friends(role: Role): Role[] {
  return Object.keys(TALKS)
    .map((k) => k.split('|') as [Role, Role])
    .filter(([a, b]) => a === role || b === role)
    .map(([a, b]) => (a === role ? b : a));
}

/** Una conversazione fra due ruoli, con chi parla per primo in testa. `used` evita le ripetizioni
 * finché ce ne sono altre. */
export function pickTalk(a: Role, b: Role, used: Set<string>): Line[] {
  const k = key(a, b);
  const pool = TALKS[k] ?? SMALL;
  const fresh = pool.map((_, i) => `${k}#${i}`).filter((id) => !used.has(id));
  const id = fresh.length
    ? fresh[Math.floor(Math.random() * fresh.length)]
    : `${k}#${Math.floor(Math.random() * pool.length)}`;
  used.add(id);
  if (fresh.length <= 1) for (const u of [...used]) if (u.startsWith(k)) used.delete(u);
  const lines = pool[Number(id.split('#')[1])];
  // nelle coppie di lavoro il ruolo «0» è il primo in ordine alfabetico
  const swap = TALKS[k] ? [a, b].sort()[0] !== a : false;
  return lines.map(([who, text]) => [(swap ? 1 - who : who) as 0 | 1, text]);
}

export interface Place {
  id: string;
  x: number;
  y: number;
  /** chi ci va più volentieri; vuoto = tutti */
  likes: Role[];
  says: string[];
  /** secondi di sosta */
  stay: [number, number];
}

/** dove si va a fare qualcosa: tutti i punti stanno su ciottoli camminabili */
export const PLACES: Place[] = [
  {
    id: 'cafe',
    x: 1015,
    y: 285,
    likes: [],
    says: ['Un cappuccino, grazie', 'Pausa caffè!', 'Che profumo di caffè'],
    stay: [6, 10],
  },
  {
    id: 'colors',
    x: 255,
    y: 410,
    likes: ['web-designer', 'ux-ui'],
    says: ['Mi serve un blu nuovo', 'Un tocco di colore', 'Prendo i pennelli'],
    stay: [6, 10],
  },
  {
    id: 'flowers-left',
    x: 300,
    y: 690,
    likes: [],
    says: ['Che bei fiori', 'Sono sbocciati!'],
    stay: [4, 7],
  },
  {
    id: 'flowers-right',
    x: 1300,
    y: 600,
    likes: [],
    says: ['Che profumo', 'Le margherite!'],
    stay: [4, 7],
  },
  {
    id: 'bridge',
    x: 1450,
    y: 770,
    likes: ['researcher', 'writer'],
    says: ['Si sente il mare', 'Qui si pensa bene'],
    stay: [5, 8],
  },
  {
    id: 'gate',
    x: 825,
    y: 900,
    likes: ['mail-clerk', 'notifier'],
    says: ['Arriva la posta', 'Consegna in centro'],
    stay: [3, 6],
  },
];
